import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '@shared/atoms/Button';
import FormAutocompleteWithList from '@shared/atoms/FormFields/FormAutocompleteWithList';
import FormSelect from '@shared/atoms/FormFields/FormSelect';
import { AUTOCOMPLETEWITHLIST_TYPE, courseStatus } from '@shared/const';
import theme from '@shared/themes/default';
import { newSelectTranslation } from '@shared/utils';
import { useAppGlobals } from '../../../../contexts/AppContext';

import { updateFeaturedCourses } from '../../graphql/mutations';
import { listCourses, listFeaturedCourses } from '../../graphql/queries';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
  max-width: 600px;
  padding: ${theme.spacing.s};
`;

const FeaturedItemTab = ({
  item,
  setErrorMessage,
  setHasError,
  setHasSuccess,
  setSuccessMessage,
}) => {
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [promotedType, setPromotedType] = useState('none');
  const [selectedFeaturedCourses, setSelectedFeaturedCourses] = useState([]);

  const { locale, useGraphQLMutation, useNewGraphQLQuery } = useAppGlobals();
  const { t } = useTranslation();

  const coursesLimit = 2000;

  const options = [
    {
      label: t('backoffice.organizations.featuredItem.none'),
      key: 'none',
      value: 'none',
    },
    {
      label: t('backoffice.organizations.featuredItem.promotedCourse'),
      key: 'course',
      value: 'course',
    },
  ];

  const listCoursesResponse = useNewGraphQLQuery(listCourses, {
    variables: {
      pagination: { limit: coursesLimit },
      filter: {
        status: courseStatus.PUBLISHED,
      },
      organizationId: item.id,
    },
  }); // TODO: Check if the list must be refetched after changing organization settings
  const listFeaturedCoursesResponse = useNewGraphQLQuery(listFeaturedCourses, {
    variables: {
      filter: {
        organizationId: item.id,
      },
    },
  });
  const [updateFeaturedCoursesMutation, updateFeaturedCoursesResponse] =
    useGraphQLMutation(updateFeaturedCourses, {
      update(cache, { data: { updateFeaturedCourses } }) {
        cache.writeQuery({
          query: listFeaturedCourses,
          data: { listFeaturedCourses: updateFeaturedCourses },
          variables: { filter: { organizationId: item.id } },
        });
      },
      onCompleted({ updateFeaturedCourses }) {
        setFeaturedCourses(updateFeaturedCourses);
        setSuccessMessage(
          t(
            updateFeaturedCourses.length > 0
              ? 'backoffice.organizations.featuredItem.successfullyAdded'
              : 'backoffice.organizations.featuredItem.successfullyRemoved'
          )
        );
        setHasSuccess(true);
      },
      onError() {
        setErrorMessage(t('backoffice.organizations.featuredItem.error'));
        setHasError(true);
      },
    });

  useEffect(() => {
    const { data, loading } = listFeaturedCoursesResponse;

    if (!loading && data) {
      setFeaturedCourses(data.listFeaturedCourses);
      setSelectedFeaturedCourses(data.listFeaturedCourses.map(({ id }) => id));

      if (data.listFeaturedCourses.length > 0) {
        setPromotedType('course');
      }
    }
  }, [listFeaturedCoursesResponse]);

  useEffect(() => {
    const { data, error, loading } = listCoursesResponse;

    if (!loading && (data || error)) {
      if (error) {
        setErrorMessage(t('backoffice.organizations.featuredItem.error'));
        setHasError(true);
      } else if (
        data.listCourses.length > 0 &&
        data.listCourses.length % coursesLimit === 0
      ) {
        listCoursesResponse.fetchMore({
          variables: {
            pagination: {
              limit: coursesLimit,
              offset: data.listCourses.length,
            },
          },
        });
      }
    }
  }, [
    listCoursesResponse.data?.listCourses?.length,
    listCoursesResponse.loading,
    listCoursesResponse.error,
  ]);

  // Promoted course calls

  const handleCourseSubmit = () => {
    updateFeaturedCoursesMutation({
      variables: {
        input: {
          courseIds: promotedType === 'none' ? [] : selectedFeaturedCourses,
          organizationId: item.id,
        },
      },
    });
  };

  return (
    <Wrapper>
      <FormSelect
        id="type"
        name="type"
        items={options}
        disabled={updateFeaturedCoursesResponse.loading}
        label={t('backoffice.organizations.featuredItem.type')}
        handleChange={({ value }) => setPromotedType(value)}
        required
        theme="dark"
        value={promotedType}
      />
      {promotedType === 'none' && (
        <Button
          ariaLabel={t('generic.save')}
          disabled={
            updateFeaturedCoursesResponse.loading ||
            featuredCourses.length === 0
          }
          loading={updateFeaturedCoursesResponse.loading}
          loadingOnIcon={updateFeaturedCoursesResponse.loading}
          onClick={handleCourseSubmit}
          sx={{ alignSelf: 'end' }}
          theme="dark"
        >
          {t('generic.save')}
        </Button>
      )}
      {promotedType === 'course' && (
        <>
          <FormAutocompleteWithList
            id="promoted-course"
            name="promoted-course"
            disabled={updateFeaturedCoursesResponse.loading}
            items={(listCoursesResponse.data?.listCourses || [])
              .map(({ id, name }) => ({
                key: id,
                title: newSelectTranslation(
                  name,
                  locale,
                  `<No name, ID: ${id}>`
                ),
              }))
              .filter((course) => !course.archivedScheduledAt)}
            label={t('backoffice.organizations.featuredItem.promotedCourse')}
            limit={1}
            loading={
              listCoursesResponse.loading && listFeaturedCoursesResponse.loading
            }
            loadingText={t('generic.loadingText')}
            noOptionsText={t('generic.noSearchResults')}
            onChange={(courseIds) => {
              setSelectedFeaturedCourses(courseIds.map(({ key }) => key));
            }}
            required
            selectedItems={(listCoursesResponse.data?.listCourses || [])
              .filter((course) => selectedFeaturedCourses.includes(course.id))
              .map(({ id, name }) => ({
                key: id,
                title: newSelectTranslation(
                  name,
                  locale,
                  `<No name, ID: ${id}>`
                ),
              }))}
            theme="dark"
            type={AUTOCOMPLETEWITHLIST_TYPE.FORM}
          />
          <Button
            ariaLabel={t('generic.save')}
            disabled={
              updateFeaturedCoursesResponse.loading ||
              selectedFeaturedCourses.length === 0 ||
              (featuredCourses.length > 0 &&
                featuredCourses.every((course) =>
                  selectedFeaturedCourses.includes(course.id)
                ))
            }
            loading={updateFeaturedCoursesResponse.loading}
            loadingOnIcon={updateFeaturedCoursesResponse.loading}
            onClick={handleCourseSubmit}
            sx={{ alignSelf: 'end' }}
            theme="dark"
          >
            {t('generic.save')}
          </Button>
        </>
      )}
    </Wrapper>
  );
};

FeaturedItemTab.propTypes = {
  item: PropTypes.object.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setHasError: PropTypes.func.isRequired,
  setHasSuccess: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};

export default FeaturedItemTab;
